import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatInr } from '@/lib/utils';
import { notifyGlobal } from '@/context/NotificationContext';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  Sparkles,
  Bot,
  TrendingUp,
  CheckCircle2,
  ShieldAlert,
  IndianRupee,
  RefreshCw,
  Download,
  Send,
  Cpu,
  Zap,
  Search,
  ArrowRight,
  Clock,
  Check,
  Loader2,
  ShieldCheck,
  FileCheck,
  UserCheck,
} from 'lucide-react';

// Types
type AiCategory =
  | 'ALL'
  | 'OPTIMIZATION'
  | 'ANOMALY_DETECTION'
  | 'PREDICTIVE_SUPPLY'
  | 'FRAUD_PREVENTION'
  | 'TREASURY_CASH_FLOW';

interface AiRecommendation {
  id: string;
  category: AiCategory;
  categoryLabel: string;
  confidenceScore: number;
  title: string;
  summary: string;
  potentialValue: number;
  valueType: 'SAVINGS' | 'RISK_AVOIDANCE' | 'WORKING_CAPITAL';
  reasoning: string[];
  suggestedAction: string;
  status: 'PENDING' | 'EXECUTING' | 'EXECUTED';
  operatingPlant: string;
  model: string;
}

interface CopilotMessage {
  id: string;
  sender: 'USER' | 'AI';
  text: string;
  timestamp: string;
  metrics?: { label: string; value: string }[];
}

export const AiPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'RECOMMENDATIONS' | 'COPILOT' | 'ANOMALIES' | 'PREDICTIONS' | 'GOVERNANCE'>('RECOMMENDATIONS');
  const [selectedCategory, setSelectedCategory] = useState<AiCategory>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  // Recommendations State
  const [recommendations, setRecommendations] = useState<AiRecommendation[]>([
    {
      id: 'ai-rec-101',
      category: 'OPTIMIZATION',
      categoryLabel: 'Volume Sourcing Consolidation',
      confidenceScore: 0.96,
      title: 'Consolidate Carbon Steel Plates across Mumbai & Pune Plants',
      summary: 'Combine 14 pending requisitions for IS 2062 Grade Steel across Plant 1010 and Plant 1020 to unlock Tier-1 volume discount from Tata Steel Ltd.',
      potentialValue: 2850000,
      valueType: 'SAVINGS',
      reasoning: [
        'Consolidated volume of 240 MT exceeds Tier-1 threshold (200+ MT).',
        'Direct mill-route dispatch saves ₹3.8 Lakhs in regional distributor handling.',
        'Tata Steel SLA compliance rating is 97.4% with zero quality quarantine flags.',
      ],
      suggestedAction: 'Auto-Generate Consolidated RFQ Package',
      status: 'PENDING',
      operatingPlant: 'Plants 1010 & 1020',
      model: 'ProcureAI-Sourcing-v4.2',
    },
    {
      id: 'ai-rec-102',
      category: 'ANOMALY_DETECTION',
      categoryLabel: 'Contract Rate Card Variance',
      confidenceScore: 0.98,
      title: 'Price Spike Detected on Workshop Emergency Valves',
      summary: 'Spot purchase requisition REQ-1049 quotes high-pressure ball valves at +34.1% premium over active Long-Term Agreement (LTA) rate cards.',
      potentialValue: 1420000,
      valueType: 'RISK_AVOIDANCE',
      reasoning: [
        'Spot quote unit price is ₹42,500 vs contracted rate of ₹31,700.',
        'Contracted supplier Kirloskar Brothers has active stock available with 48h lead time.',
        'Prevents maverick off-catalog spend leakage at Plant 1040 (NCR).',
      ],
      suggestedAction: 'Reroute to Contracted LTA Catalog',
      status: 'PENDING',
      operatingPlant: 'Plant 1040 (NCR)',
      model: 'ProcureAI-LeakageAudit-v3',
    },
    {
      id: 'ai-rec-103',
      category: 'PREDICTIVE_SUPPLY',
      categoryLabel: 'Transit Bottleneck & Buffer Stock',
      confidenceScore: 0.92,
      title: 'Predicted Monsoon Logistics Delay for Chennai Port Inbound',
      summary: 'Coastal weather telemetry indicates high probability of 5-7 days delay for imported hydraulic actuators scheduled for Plant 1030.',
      potentialValue: 4500000,
      valueType: 'RISK_AVOIDANCE',
      reasoning: [
        'Historical weather pattern analysis correlates with 8-day port clearing lag.',
        'Advancing supplier dispatch by 72 hours prevents critical plant boiler downtime.',
        'Warehouse safe-stock threshold will breach minimum buffer in 9 days.',
      ],
      suggestedAction: 'Advance PO Dispatch & Book Express Freight',
      status: 'PENDING',
      operatingPlant: 'Plant 1030 (Chennai)',
      model: 'ProcureAI-LeadTime-Predictor',
    },
    {
      id: 'ai-rec-104',
      category: 'FRAUD_PREVENTION',
      categoryLabel: 'Duplicate Invoice Interception',
      confidenceScore: 0.99,
      title: 'Probable Duplicate Invoice Intercepted: INV-9904 vs INV-7002',
      summary: 'Vendor invoice INV-9904 submitted with identical line items, delivery challan reference, and net amount (₹12,80,000) as paid voucher INV-7002.',
      potentialValue: 1280000,
      valueType: 'RISK_AVOIDANCE',
      reasoning: [
        'Optical Character Recognition (OCR) match on line item description and PO number.',
        'Invoice dates are 12 days apart with identical tax breakdown.',
        'Directly stops fraudulent or accidental duplicate clearing in SAP F110.',
      ],
      suggestedAction: 'Place Immediate Hard Payment Hold',
      status: 'PENDING',
      operatingPlant: 'Plant 1010 (Mumbai)',
      model: 'ProcureAI-AuditShield-v5',
    },
    {
      id: 'ai-rec-105',
      category: 'TREASURY_CASH_FLOW',
      categoryLabel: 'Dynamic Cash Discount Yield',
      confidenceScore: 0.94,
      title: 'Capture Early Settlement Discount: Siemens Industrial 2.5%',
      summary: 'Early disbursement of approved invoice INV-7003 within next 48 hours unlocks 2.5% cash discount, yielding an annualized return of 18.2%.',
      potentialValue: 640000,
      valueType: 'WORKING_CAPITAL',
      reasoning: [
        'Contract payment terms: 2.5% 10 Net 45.',
        'Current corporate treasury cash float is surplus by ₹14.5 Cr.',
        'Generates immediate EBITDA enhancement with zero commercial friction.',
      ],
      suggestedAction: 'Queue Disbursement for Immediate F110 Batch',
      status: 'PENDING',
      operatingPlant: 'All Operating Units',
      model: 'ProcureAI-CashYield-v2',
    },
  ]);

  // Copilot Interactive Chat State
  const [copilotInput, setCopilotInput] = useState('');
  const [isCopilotTyping, setIsCopilotTyping] = useState(false);
  const [copilotMessages, setCopilotMessages] = useState<CopilotMessage[]>([
    {
      id: 'msg-1',
      sender: 'AI',
      text: 'Hello! I am your ProcureAI Cognitive Sourcing Copilot. I continuously analyze your ERP purchase orders, supplier contracts, invoice variances, and logistics feeds. How can I assist your procurement decisions today?',
      timestamp: 'Just now',
      metrics: [
        { label: 'Real-time Monitored Spend', value: '₹48.65 Cr' },
        { label: 'Active Anomaly Watchdogs', value: '18 Models' },
        { label: 'Identified Savings', value: '₹3.85 Cr' },
      ],
    },
  ]);

  // Handle Scanning Refresh
  const handleRunScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      notifyGlobal(
        'AI Diagnostic Scan Complete',
        'All 18 autonomous neural models synchronized with active SAP S/4HANA transactional ledger.',
        'StatusChange'
      );
    }, 900);
  };

  // Handle Action Execution
  const handleExecuteAction = (recId: string) => {
    const target = recommendations.find((r) => r.id === recId);
    setRecommendations((prev) =>
      prev.map((r) => (r.id === recId ? { ...r, status: 'EXECUTING' } : r))
    );

    setTimeout(() => {
      setRecommendations((prev) =>
        prev.map((r) => (r.id === recId ? { ...r, status: 'EXECUTED' } : r))
      );
      if (target) {
        notifyGlobal(
          'Autonomous Action Executed in ERP',
          `Executed "${target.suggestedAction}" for ${target.operatingPlant}.`,
          'StatusChange',
          target.id
        );
      }
    }, 900);
  };

  // Handle Export Dossier
  const handleExportInsights = () => {
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(
        JSON.stringify(
          {
            exportTimestamp: new Date().toISOString(),
            engineVersion: 'ProcureAI Autonomous Sourcing v4.2',
            identifiedSavings: '₹3.85 Cr',
            totalAutonomousApprovals: 1248,
            recommendations,
          },
          null,
          2
        )
      );
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', `ProcureAI_Cognitive_Intelligence_Dossier_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notifyGlobal(
      'AI Dossier Exported',
      'Cognitive intelligence dossier downloaded successfully.',
      'StatusChange'
    );
  };

  // Copilot Chat Submission
  const handleSendCopilotMessage = (promptText?: string) => {
    const textToSend = promptText || copilotInput;
    if (!textToSend.trim()) return;

    const userMsg: CopilotMessage = {
      id: `user-${Date.now()}`,
      sender: 'USER',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setCopilotMessages((prev) => [...prev, userMsg]);
    if (!promptText) setCopilotInput('');
    setIsCopilotTyping(true);

    setTimeout(() => {
      let aiReply = '';
      let metrics: { label: string; value: string }[] | undefined;

      const lower = textToSend.toLowerCase();

      // AI RBAC: Prevent unauthorized cross-plant procurement data leakage
      const targetPlantMatch = textToSend.match(/plant\s*([a-zA-Z0-9_-]+)/i);
      if (
        targetPlantMatch &&
        targetPlantMatch[1] &&
        user &&
        user.role !== 'ADMIN' &&
        user.accessScope?.ownership !== 'ENTERPRISE'
      ) {
        const queryPlant = targetPlantMatch[1].toUpperCase();
        const userPlants = (user.accessScope?.plants || []).map((p) => p.toUpperCase());
        const isAuthorizedPlant = userPlants.some((p) => p.includes(queryPlant) || queryPlant.includes(p.replace('PLANT-', '')));
        if (!isAuthorizedPlant) {
          aiReply = `🔒 Scope Authorization Guard: Your account scope is restricted to ${user.accessScope?.plants?.join(', ') || user.plantCode}. In accordance with ProcureAI Enterprise RBAC policy, cross-plant intelligence queries for unauthorized facilities (Plant ${targetPlantMatch[1]}) are strictly blocked.`;
          metrics = [
            { label: 'Scope Authorization', value: 'Denied' },
            { label: 'Permitted Scope', value: user.accessScope?.plants?.join(', ') || user.plantCode },
          ];
          const deniedMsg: CopilotMessage = {
            id: `ai-${Date.now()}`,
            sender: 'AI',
            text: aiReply,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            metrics,
          };
          setCopilotMessages((prev) => [...prev, deniedMsg]);
          setIsCopilotTyping(false);
          return;
        }
      }

      if (lower.includes('steel') || lower.includes('tata')) {
        aiReply = 'Tata Steel Ltd is currently your highest performing raw metallurgy supplier with an OTIF score of 97.4% and zero quality defect notices across 88 Purchase Orders. I recommend consolidating your upcoming Q1 structural steel requirements (approx. 240 MT) to negotiate an additional 6.8% volume rebate.';
        metrics = [
          { label: 'Contracted Supplier', value: 'Tata Steel Ltd' },
          { label: 'OTIF Performance', value: '97.4%' },
          { label: 'Recommended Discount', value: '6.8% (₹28.5L)' },
        ];
      } else if (lower.includes('variance') || lower.includes('price')) {
        aiReply = 'Current Purchase Price Variance (PPV) is running favorable at -₹1.35 Cr (-2.8% vs standard baseline). However, MRO workshop emergency valves in Plant 1040 show an unfavorable +34.1% spot premium. Rerouting those POs to Kirloskar Brothers LTA saves ₹14.2 Lakhs.';
        metrics = [
          { label: 'Overall PPV', value: '-₹1.35 Cr (Favorable)' },
          { label: 'Unfavorable Outlier', value: '+34.1% (Spot Valves)' },
        ];
      } else if (lower.includes('cash') || lower.includes('discount')) {
        aiReply = 'You have 4 approved invoices totaling ₹2.56 Cr eligible for 2-2.5% dynamic early settlement discounts if cleared within 4 days. Capturing these yields an annualized treasury return of 18.2% on idle working capital.';
        metrics = [
          { label: 'Eligible Invoices', value: '4 Invoices' },
          { label: 'Discount Value', value: '₹6.40 Lakhs' },
          { label: 'Annualized ROI', value: '18.2%' },
        ];
      } else {
        aiReply = `I have audited your enterprise procurement data against your query: "${textToSend}". All active Purchase Orders, Goods Receipts, and Invoices have been scanned against DOA thresholds and SAP S/4HANA clearing rules. No unmitigated commercial risks were identified outside the active recommendation matrix.`;
      }

      const aiMsg: CopilotMessage = {
        id: `ai-${Date.now()}`,
        sender: 'AI',
        text: aiReply,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        metrics,
      };

      setCopilotMessages((prev) => [...prev, aiMsg]);
      setIsCopilotTyping(false);
    }, 850);
  };

  // Filtered Recommendations
  const filteredRecommendations = useMemo(() => {
    return recommendations.filter((r) => {
      const matchCat = selectedCategory === 'ALL' || r.category === selectedCategory;
      const matchQuery =
        !searchQuery ||
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.operatingPlant.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchQuery;
    });
  }, [recommendations, selectedCategory, searchQuery]);

  return (
    <div className="space-y-6 pb-20 font-poppins">
      {/* Top Executive Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-2xl bg-gradient-to-tr from-primary to-purple-600 text-white shadow-md shadow-primary/20">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-extrabold tracking-tight text-foreground font-poppins">
                  ProcureAI Cognitive Sourcing & Intelligence
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-poppins">
                  <Cpu className="h-3 w-3" /> Autonomous Engine v4.2 Active
                </span>
              </div>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
                Continuous machine learning models for volume bundling, supplier risk forecasting, duplicate invoice defense, and automated commercial decisions.
              </p>
            </div>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunScan}
            disabled={isScanning}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isScanning ? 'animate-spin' : ''}`} />
            {isScanning ? 'Scanning Telemetry...' : 'Run Sourcing Diagnostic'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveTab('COPILOT')}
            className="rounded-xl font-bold font-poppins cursor-pointer border-primary/30 text-primary hover:bg-primary/10"
          >
            <Bot className="h-3.5 w-3.5 mr-1.5" /> Sourcing Copilot
          </Button>

          <Button
            size="sm"
            onClick={handleExportInsights}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export Insights
          </Button>
        </div>
      </div>

      {/* 4 AI Telemetry & Impact KPI Cards (INR ₹) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-poppins">
        {/* Card 1: Identified Cost Savings */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
                AI Identified Savings
              </span>
              <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
                <TrendingUp className="h-4 w-4" />
              </div>
            </div>
            <div className="text-2xl font-black text-emerald-600 font-mono">
              ₹3.85 Cr
            </div>
            <p className="text-[10px] font-bold text-emerald-600/90">Across 18 active bundling opportunities</p>
          </CardContent>
        </Card>

        {/* Card 2: Autonomous Decisions */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-primary">
                Autonomous Approvals
              </span>
              <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                <Zap className="h-4 w-4" />
              </div>
            </div>
            <div className="text-2xl font-black text-foreground font-mono">
              1,248 Tasks
            </div>
            <p className="text-[10px] font-bold text-foreground-muted">Touchless PO & GRN matches (DOA &lt; ₹5L)</p>
          </CardContent>
        </Card>

        {/* Card 3: Fraud & Variance Blocked */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-purple-600">
                Risk & Fraud Blocked
              </span>
              <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600">
                <ShieldAlert className="h-4 w-4" />
              </div>
            </div>
            <div className="text-2xl font-black text-purple-700 font-mono">
              ₹84.2 Lakhs
            </div>
            <p className="text-[10px] font-bold text-purple-600/90">14 duplicate invoices & spot spikes intercepted</p>
          </CardContent>
        </Card>

        {/* Card 4: Model Confidence */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-blue-600">
                Model Precision Score
              </span>
              <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600">
                <Cpu className="h-4 w-4" />
              </div>
            </div>
            <div className="text-2xl font-black text-blue-700 font-mono">
              96.8%
            </div>
            <p className="text-[10px] font-bold text-foreground-muted">Benchmarked against senior CPO signoffs</p>
          </CardContent>
        </Card>
      </div>

      {/* Segmented Squeezed Intelligence Tabs */}
      <div className="flex border-b border-border overflow-x-auto gap-1.5 text-xs font-poppins pb-1">
        {[
          { key: 'RECOMMENDATIONS', label: 'Actionable Sourcing Recommendations', icon: Sparkles },
          { key: 'COPILOT', label: 'Interactive Sourcing Copilot', icon: Bot },
          { key: 'ANOMALIES', label: 'Anomaly & Fraud Defense Matrix', icon: ShieldAlert },
          { key: 'PREDICTIONS', label: 'Predictive Demand & Bottlenecks', icon: Clock },
          { key: 'GOVERNANCE', label: 'Autonomous Decision Guardrails', icon: ShieldCheck },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer font-poppins ${
                isActive
                  ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                  : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* TAB 1: ACTIONABLE SOURCING RECOMMENDATIONS */}
      {activeTab === 'RECOMMENDATIONS' && (
        <div className="space-y-4 font-poppins">
          {/* Squeezed Filter Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-muted/20 p-3 rounded-2xl border border-border">
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All AI Opportunities' },
                { key: 'OPTIMIZATION', label: 'Volume Bundling' },
                { key: 'ANOMALY_DETECTION', label: 'Contract Rate Card Variance' },
                { key: 'PREDICTIVE_SUPPLY', label: 'Predictive Transit' },
                { key: 'FRAUD_PREVENTION', label: 'Duplicate Defense' },
                { key: 'TREASURY_CASH_FLOW', label: 'Cash Yield' },
              ].map((pill) => (
                <button
                  key={pill.key}
                  onClick={() => setSelectedCategory(pill.key as AiCategory)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer transition-all font-poppins ${
                    selectedCategory === pill.key
                      ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                      : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search AI opportunities, plant, model..."
                className="w-full h-8.5 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>

          {/* Recommendations Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredRecommendations.length === 0 ? (
              <div className="lg:col-span-2 p-12 text-center text-xs text-foreground-muted border border-border rounded-2xl bg-card">
                No active AI recommendations match your filter criteria.
              </div>
            ) : (
              filteredRecommendations.map((rec) => {
                const isExecuted = rec.status === 'EXECUTED';
                const isExecuting = rec.status === 'EXECUTING';

                return (
                  <Card
                    key={rec.id}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden shadow-2xs ${
                      isExecuted
                        ? 'border-emerald-500/40 bg-emerald-500/5'
                        : 'border-border bg-card hover:border-primary/40'
                    }`}
                  >
                    <CardHeader className="p-4 border-b border-border/60 bg-muted/20 pb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                          <Bot className="h-4 w-4 text-primary" /> {rec.categoryLabel}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-emerald-500/10 text-emerald-600 font-extrabold text-[11px] px-2.5 py-0.5 border border-emerald-500/20 font-mono">
                            {Math.round(rec.confidenceScore * 100)}% Confidence
                          </span>
                          <span className="text-[10px] text-foreground-muted font-mono">
                            {rec.operatingPlant}
                          </span>
                        </div>
                      </div>
                      <CardTitle className="text-sm font-bold text-foreground mt-2 font-poppins">
                        {rec.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-4 space-y-3.5 text-xs font-poppins">
                      <p className="text-foreground-secondary leading-relaxed">{rec.summary}</p>

                      {/* Commercial Value Badge */}
                      <div className="flex items-center justify-between p-2.5 rounded-xl bg-card border border-border/80">
                        <span className="text-[11px] font-bold text-foreground-muted uppercase">
                          {rec.valueType === 'SAVINGS'
                            ? 'Estimated Savings Impact'
                            : rec.valueType === 'RISK_AVOIDANCE'
                            ? 'Risk Avoidance Value'
                            : 'Working Capital Yield'}
                        </span>
                        <span className="font-mono font-black text-sm text-emerald-600 flex items-center">
                          <IndianRupee className="h-3.5 w-3.5" />
                          {formatInr(rec.potentialValue)}
                        </span>
                      </div>

                      {/* Reasoning Bullets */}
                      <div className="space-y-1.5 bg-muted/30 p-3 rounded-xl border border-border/60">
                        <p className="font-bold text-foreground text-[11px] flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5 text-primary" /> Model Rationale & Verification Telemetry:
                        </p>
                        <ul className="space-y-1 text-foreground-secondary text-[11px] list-disc pl-4">
                          {rec.reasoning.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Suggested Action Bar */}
                      <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-border/60">
                        <div className="min-w-0">
                          <span className="text-[10px] text-foreground-muted uppercase font-bold block">
                            Recommended Execution
                          </span>
                          <p className="font-bold text-foreground truncate text-xs">
                            {rec.suggestedAction}
                          </p>
                        </div>

                        <Button
                          size="sm"
                          disabled={isExecuted || isExecuting}
                          onClick={() => handleExecuteAction(rec.id)}
                          className={`rounded-xl font-bold font-poppins cursor-pointer text-xs shrink-0 transition-all ${
                            isExecuted
                              ? 'bg-emerald-600 text-white hover:bg-emerald-600'
                              : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs'
                          }`}
                        >
                          {isExecuting ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Executing Action...
                            </>
                          ) : isExecuted ? (
                            <>
                              <Check className="h-3.5 w-3.5 mr-1.5" /> Action Executed in ERP
                            </>
                          ) : (
                            <>
                              <Zap className="h-3.5 w-3.5 mr-1.5" /> Execute Autonomous Action
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* TAB 2: INTERACTIVE SOURCING COPILOT */}
      {activeTab === 'COPILOT' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-poppins">
          {/* Chat Window */}
          <Card className="lg:col-span-2 rounded-2xl border-border bg-card shadow-xs flex flex-col h-[580px]">
            <CardHeader className="p-4 border-b border-border/60 bg-muted/20 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-xl bg-primary/10 text-primary">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-bold text-foreground">
                      ProcureAI Sourcing Copilot
                    </CardTitle>
                    <p className="text-[11px] text-foreground-muted">
                      Ask questions in natural language across purchase orders, suppliers, and ERP contracts.
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                  Online
                </span>
              </div>
            </CardHeader>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar text-xs">
              {copilotMessages.map((msg) => {
                const isAi = msg.sender === 'AI';
                return (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${isAi ? 'justify-start' : 'justify-end'}`}
                  >
                    {isAi && (
                      <div className="h-8 w-8 rounded-xl bg-gradient-to-tr from-primary to-purple-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                        <Bot className="h-4 w-4" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl p-3.5 space-y-2.5 ${
                        isAi
                          ? 'bg-muted/40 border border-border text-foreground'
                          : 'bg-primary text-primary-foreground font-medium'
                      }`}
                    >
                      <p className="leading-relaxed">{msg.text}</p>

                      {/* Render Metrics Pill if present */}
                      {msg.metrics && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-border/40">
                          {msg.metrics.map((m, idx) => (
                            <div key={idx} className="p-2 rounded-lg bg-card border border-border/60">
                              <span className="text-[9px] font-bold text-foreground-muted uppercase block truncate">
                                {m.label}
                              </span>
                              <span className="font-mono font-bold text-xs text-foreground block truncate">
                                {m.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <span className="text-[10px] opacity-70 block text-right font-mono">
                        {msg.timestamp}
                      </span>
                    </div>
                  </div>
                );
              })}

              {isCopilotTyping && (
                <div className="flex gap-3 items-center text-xs text-foreground-muted">
                  <div className="h-7 w-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Bot className="h-3.5 w-3.5 animate-spin" />
                  </div>
                  <span className="animate-pulse">ProcureAI is analyzing ERP database...</span>
                </div>
              )}
            </div>

            {/* Input Bar */}
            <div className="p-3 border-t border-border/60 bg-muted/10 shrink-0">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendCopilotMessage();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={copilotInput}
                  onChange={(e) => setCopilotInput(e.target.value)}
                  placeholder="Ask ProcureAI e.g. 'Compare steel suppliers by OTIF and cost'..."
                  className="flex-1 h-9 px-3 text-xs rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!copilotInput.trim() || isCopilotTyping}
                  className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer h-9 px-4"
                >
                  <Send className="h-3.5 w-3.5 mr-1" /> Send
                </Button>
              </form>
            </div>
          </Card>

          {/* Copilot Prompt Suggestions & Watchdogs */}
          <div className="space-y-4">
            <Card className="rounded-2xl border-border bg-card shadow-xs">
              <CardHeader className="p-4 border-b border-border/60">
                <CardTitle className="text-sm font-bold text-foreground">
                  Suggested Intelligent Prompts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2 text-xs">
                {[
                  'Which suppliers have >95% OTIF for heavy engineering?',
                  'Find open purchase requests with unapproved budget variances',
                  'Analyze price spikes and maverick leakage for Q4',
                  'Which invoices can unlock dynamic cash discounts this week?',
                ].map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSendCopilotMessage(prompt)}
                    className="w-full text-left p-2.5 rounded-xl border border-border bg-muted/20 hover:bg-muted/60 text-foreground transition-all flex items-center justify-between group cursor-pointer"
                  >
                    <span className="truncate pr-2 font-medium">{prompt}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border bg-card shadow-xs">
              <CardHeader className="p-4 border-b border-border/60">
                <CardTitle className="text-sm font-bold text-foreground">
                  Active Autonomous Watchdogs
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2.5 text-xs">
                <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold text-emerald-700 block">3-Way Match Tolerances</span>
                    <span className="text-[10px] text-foreground-muted">Auto-approves within 1% price & qty threshold</span>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                </div>

                <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold text-blue-700 block">DOA Fast-Track Approval</span>
                    <span className="text-[10px] text-foreground-muted">Auto-clears catalog items under ₹1,00,000</span>
                  </div>
                  <Zap className="h-4 w-4 text-blue-600 shrink-0" />
                </div>

                <div className="p-2.5 bg-purple-500/10 border border-purple-500/20 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold text-purple-700 block">Sanctions & Phantom Vendor Check</span>
                    <span className="text-[10px] text-foreground-muted">Scans GSTIN and MCA registrar bi-weekly</span>
                  </div>
                  <ShieldCheck className="h-4 w-4 text-purple-600 shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* TAB 3: ANOMALY & FRAUD DEFENSE MATRIX */}
      {activeTab === 'ANOMALIES' && (
        <div className="space-y-4 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
              <CardTitle className="text-sm font-bold text-foreground flex items-center justify-between">
                <span>Real-Time Fraud & Anomaly Interception Ledger</span>
                <span className="text-xs font-bold text-purple-600">Zero Commercial Breaches Recorded</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-xs font-poppins">
                <thead className="bg-muted/60 border-b border-border text-foreground-muted text-[11px] font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Telemetry Event</th>
                    <th className="py-3 px-4">Entity Reference</th>
                    <th className="py-3 px-4">Operating Plant</th>
                    <th className="py-3 px-4 text-right">Intercepted Exposure (₹)</th>
                    <th className="py-3 px-4 text-center">Confidence</th>
                    <th className="py-3 px-4 text-center">Defensive Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  <tr className="hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block">Duplicate Invoice Reference</span>
                      <span className="text-[10px] text-foreground-muted">Identical line items matching INV-7002</span>
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-purple-600">INV-9904</td>
                    <td className="py-3 px-4">Plant 1010 (Mumbai)</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-rose-600">₹12,80,000</td>
                    <td className="py-3 px-4 text-center font-bold text-emerald-600">99.4%</td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/10 text-rose-600 border border-rose-500/20">
                        Hard Payment Block
                      </span>
                    </td>
                  </tr>

                  <tr className="hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block">Sudden Price Spike (+34.1%)</span>
                      <span className="text-[10px] text-foreground-muted">Workshop valves quoted above contracted rate card</span>
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-primary">REQ-1049</td>
                    <td className="py-3 px-4">Plant 1040 (NCR)</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-amber-600">₹14,20,000</td>
                    <td className="py-3 px-4 text-center font-bold text-emerald-600">98.1%</td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                        LTA Reroute Enforced
                      </span>
                    </td>
                  </tr>

                  <tr className="hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block">Bank Account Modification Notice</span>
                      <span className="text-[10px] text-foreground-muted">Beneficiary IFSC changed prior to F110 clearing</span>
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-blue-600">VND-1004 (Kirloskar)</td>
                    <td className="py-3 px-4">Pan-India Master</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-foreground">₹28,90,000</td>
                    <td className="py-3 px-4 text-center font-bold text-emerald-600">99.9%</td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/10 text-blue-600 border border-blue-500/20">
                        Out-of-Band Phone Verified
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB 4: PREDICTIVE DEMAND & BOTTLENECK FORECASTING */}
      {activeTab === 'PREDICTIONS' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Plant-Level Safety Stock Breach Forecast
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-xs">
              <div className="p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-xl space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-amber-700">Plant 1030 (Chennai) — Hydraulic Actuators</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-800">
                    Breach in 9 Days
                  </span>
                </div>
                <p className="text-foreground-secondary text-[11px]">
                  Monsoon logistics delay threatens turbine assembly buffer. AI recommends advancing PO dispatch by 72 hours.
                </p>
              </div>

              <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-emerald-700">Plant 1010 (Mumbai) — Structural Steel 2062</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-800">
                    Optimal Buffer (38 Days)
                  </span>
                </div>
                <p className="text-foreground-secondary text-[11px]">
                  Active PO deliveries from Tata Steel Ltd are tracking on schedule with 97.4% reliability.
                </p>
              </div>

              <div className="p-3.5 bg-blue-500/10 border border-blue-500/20 rounded-xl space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-blue-700">Plant 1020 (Pune) — Boiler Pressure Vessels</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-800">
                    Healthy Float (24 Days)
                  </span>
                </div>
                <p className="text-foreground-secondary text-[11px]">
                  Fabrication milestones verified with L&T Heavy Engineering via digital QA inspection certificate.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Autonomous Lead-Time Shrinkage
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4 text-xs">
              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Average P2P Cycle Time</span>
                  <span className="text-[11px] text-foreground-muted">From PR initiation to PO release</span>
                </div>
                <span className="text-base font-black text-emerald-600 font-mono">1.8 Days (-68%)</span>
              </div>

              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Supplier Bid Response Acceleration</span>
                  <span className="text-[11px] text-foreground-muted">Automated RFQ distribution and WhatsApp reminders</span>
                </div>
                <span className="text-base font-black text-primary font-mono">4.2 Days (vs 12.0d)</span>
              </div>

              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Automated 3-Way Match Clearances</span>
                  <span className="text-[11px] text-foreground-muted">Invoices processed straight-through into SAP</span>
                </div>
                <span className="text-base font-black text-purple-600 font-mono">82.4% Touchless</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB 5: AUTONOMOUS DECISION GUARDRAILS */}
      {activeTab === 'GOVERNANCE' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Delegation of Authority (DOA) Governance
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-xs">
              <div className="p-3 bg-muted/40 rounded-xl border border-border flex items-center justify-between">
                <div>
                  <span className="font-bold text-foreground block">Level 1: Autonomous Auto-Approval</span>
                  <span className="text-[11px] text-foreground-muted">Pre-contracted catalog items with zero price variance</span>
                </div>
                <span className="font-mono font-bold text-primary">Up to ₹1,00,000</span>
              </div>

              <div className="p-3 bg-muted/40 rounded-xl border border-border flex items-center justify-between">
                <div>
                  <span className="font-bold text-foreground block">Level 2: Procurement Specialist Review</span>
                  <span className="text-[11px] text-foreground-muted">Requires single human confirmation via Copilot</span>
                </div>
                <span className="font-mono font-bold text-foreground">₹1,00,000 — ₹10,00,000</span>
              </div>

              <div className="p-3 bg-muted/40 rounded-xl border border-border flex items-center justify-between">
                <div>
                  <span className="font-bold text-foreground block">Level 3: Multi-Tier Board & CPO Signoff</span>
                  <span className="text-[11px] text-foreground-muted">Full NFA & LFA dual cryptographic authorization</span>
                </div>
                <span className="font-mono font-bold text-purple-600">Above ₹10,00,000</span>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Responsible AI & Safety Auditing
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-xs">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-emerald-700 block">Hallucination & Rate Card Guardrails</span>
                  <span className="text-[10px] text-foreground-muted">All prices strictly verified against indexed SAP ERP tables</span>
                </div>
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              </div>

              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-blue-700 block">Explainability & Audit Logs</span>
                  <span className="text-[10px] text-foreground-muted">Every AI decision writes immutable reasoning steps to audit ledger</span>
                </div>
                <FileCheck className="h-4 w-4 text-blue-600 shrink-0" />
              </div>

              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-purple-700 block">Anti-Bias Supplier Allocation</span>
                  <span className="text-[10px] text-foreground-muted">Prevents sole-source vendor monopolization across operating plants</span>
                </div>
                <UserCheck className="h-4 w-4 text-purple-600 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
